import { useLogout } from '@/lib/use-logout';
import {
  type EnumDef,
  type TahitiEnvironmentValue,
  tahitiEnvironmentValueSchema,
} from '@mss_creator/shared';
import { useState } from 'react';
import { FlagRow } from './flag-row';
import { TahitiConfirmDialog } from './tahiti-confirm-dialog';
import { useFlagMutation } from './use-flag-mutation';

interface TahitiFlagRowProps {
  def: EnumDef;
  /** The value the server holds. */
  serverValue: string;
}

/**
 * The `tahiti-environment` flag. Changing it signs every user out, so a click
 * (or "Par défaut") opens a confirmation first; only the confirm button writes.
 * After a real change the user is sent to `/login` like a logout. Choosing the
 * value already active does nothing.
 */
export function TahitiFlagRow({ def, serverValue }: TahitiFlagRowProps) {
  const { mutation, saved } = useFlagMutation(def.key);
  const logout = useLogout();
  const [target, setTarget] = useState<TahitiEnvironmentValue | null>(null);

  function requestChange(next: string): void {
    const parsed = tahitiEnvironmentValueSchema.safeParse(next);
    if (!parsed.success || next === serverValue) return;
    mutation.reset();
    setTarget(parsed.data);
  }

  function confirm(): void {
    if (target === null) return;
    mutation.mutate(target, { onSuccess: () => void logout() });
  }

  function cancel(): void {
    mutation.reset();
    setTarget(null);
  }

  return (
    <>
      <FlagRow
        def={def}
        value={serverValue}
        pending={false}
        saved={saved}
        onSelect={requestChange}
      />
      <TahitiConfirmDialog
        target={target}
        backToDefault={target === def.default}
        pending={mutation.isPending}
        failed={mutation.isError}
        onConfirm={confirm}
        onCancel={cancel}
      />
    </>
  );
}
